import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';
import { ColorPicker, ColorPickerModule } from 'primeng/colorpicker';
import { InputTextModule } from 'primeng/inputtext';
import { distinctUntilChanged, startWith } from 'rxjs';

export interface ColorPickerProps extends FormlyFieldProps {
  showValueInput?: boolean;
}

export interface FormlyColorPickerFieldConfig
  extends FormlyFieldConfig<ColorPickerProps> {
  type: 'colorpicker';
}

@Component({
  selector: 'kon-colorpicker-type',
  standalone: true,
  imports: [
    CommonModule,
    ColorPickerModule,
    ReactiveFormsModule,
    InputTextModule,
    FormsModule,
  ],
  template: ` <div class="flex items-center">
    <p-colorPicker [formControl]="formControl" [id]="id" appendTo="body" />
    @if (props.showValueInput) {
      <input
        pInputText
        type="text"
        [(ngModel)]="value"
        [id]="id + '-input'"
        class="ml-2"
      />
    }
  </div>`,
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyColorPickerTypeComponent
  extends FieldType<FieldTypeConfig<ColorPickerProps>>
  implements AfterViewInit, OnInit
{
  @ViewChild(ColorPicker) colorPicker!: ColorPicker;

  readonly #injector = inject(Injector);

  value = signal<string | undefined>(undefined);

  ngOnInit(): void {
    const destroyRef = this.#injector.get(DestroyRef);

    this.formControl.valueChanges
      .pipe(
        startWith(this.formControl.value),
        distinctUntilChanged(),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((value) => {
        const old = this.value();

        if (value !== old) {
          this.value.set(value);
        }
      });

    toObservable(this.value, { injector: this.#injector })
      .pipe(distinctUntilChanged(), takeUntilDestroyed(destroyRef))
      .subscribe((value) => {
        const old = this.formControl.value;

        if (value !== old && this.isValidColor(value || '')) {
          this.formControl.setValue(value);
        }
      });
  }

  ngAfterViewInit() {
    if (this.colorPicker) {
      this.colorPicker.defaultColor = 'ffffff';
      this.colorPicker.writeValue(this.formControl.value);
    }
  }

  private isValidColor(value: string) {
    return /^#([0-9A-F]{6})$/i.test(value);
  }
}
